import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import classNames from 'classnames';
import raf from 'rc-util/lib/raf';
import ResizeObserver from 'rc-resize-observer';
import OutlinedRight from 'acud-icon/lib/icons/OutlinedRight';
import OutlinedLeft from 'acud-icon/lib/icons/OutlinedLeft';
import findIndex from 'lodash/findIndex';
import useRaf, { useRafState } from '../hooks/useRaf';
import TabNode from './TabNode';
import useOffsets from '../hooks/useOffsets';
import useVisibleRange from '../hooks/useVisibleRange';
import TabContext from '../TabContext';
import useTouchMove from '../hooks/useTouchMove';
import useRefs from '../hooks/useRefs';
import AddButton from './AddButton';
import useSyncState from '../hooks/useSyncState';
const ExtraContent = ({ position, prefixCls, extra }) => {
    if (!extra)
        return null;
    let content;
    const assertExtra = extra;
    if (position === 'right') {
        content = assertExtra.right || (!assertExtra.left && assertExtra) || null;
    }
    if (position === 'left') {
        content = assertExtra.left || null;
    }
    return content ? <div className={`${prefixCls}-extra-content`}>{content}</div> : null;
};
function TabNavList(props, ref) {
    const { prefixCls, tabs } = React.useContext(TabContext);
    const { className, style, id, animated, activeKey, rtl, extra, editable, locale, tabPosition, tabBarGutter, mobile, children, onTabClick, onTabScroll, onEditTabNav } = props;
    const tabsWrapperRef = useRef();
    const tabListRef = useRef();
    const operationsRef = useRef();
    const innerAddButtonRef = useRef();
    const [getBtnRef, removeBtnRef] = useRefs();
    const tabPositionTopOrBottom = tabPosition === 'top' || tabPosition === 'bottom';
    const [transformLeft, setTransformLeft] = useSyncState(0, (next, prev) => {
        if (tabPositionTopOrBottom && onTabScroll) {
            onTabScroll({ direction: next > prev ? 'left' : 'right' });
        }
    });
    const [transformTop, setTransformTop] = useSyncState(0, (next, prev) => {
        if (!tabPositionTopOrBottom && onTabScroll) {
            onTabScroll({ direction: next > prev ? 'top' : 'bottom' });
        }
    });
    const [wrapperScrollWidth, setWrapperScrollWidth] = useState(0);
    const [wrapperScrollHeight, setWrapperScrollHeight] = useState(0);
    const [wrapperContentWidth, setWrapperContentWidth] = useState(0);
    const [wrapperContentHeight, setWrapperContentHeight] = useState(0);
    const [wrapperWidth, setWrapperWidth] = useState(null);
    const [wrapperHeight, setWrapperHeight] = useState(null);
    const [addWidth, setAddWidth] = useState(0);
    const [addHeight, setAddHeight] = useState(0);
    const [isScroll, setScroll] = useState(true);
    const [tabSizes, setTabSizes] = useRafState(new Map());
    const tabOffsets = useOffsets(tabs, tabSizes, wrapperScrollWidth);
    // ========================== Util =========================
    const operationsHiddenClassName = `${prefixCls}-nav-operations-hidden`;
    let transformMin = 0;
    let transformMax = 0;
    if (!tabPositionTopOrBottom) {
        transformMin = Math.min(0, wrapperHeight - wrapperScrollHeight);
        transformMax = 0;
    }
    else if (rtl) {
        transformMin = 0;
        transformMax = Math.max(0, wrapperScrollWidth - wrapperWidth);
    }
    else {
        transformMin = Math.min(0, wrapperWidth - wrapperScrollWidth);
        transformMax = 0;
    }
    function alignInRange(value) {
        if (value < transformMin) {
            return transformMin;
        }
        if (value > transformMax) {
            return transformMax;
        }
        return value;
    }
    // ========================= Mobile ========================
    const touchMovingRef = useRef();
    const [lockAnimation, setLockAnimation] = useState();
    function doLockAnimation() {
        setLockAnimation(Date.now());
    }
    function clearTouchMoving() {
        window.clearTimeout(touchMovingRef.current);
    }
    useTouchMove(tabsWrapperRef, (offsetX, offsetY) => {
        function doMove(setState, offset) {
            setState(value => {
                const newValue = alignInRange(value + offset);
                return newValue;
            });
        }
        if (tabPositionTopOrBottom) {
            // Skip scroll if place is enough
            if (wrapperWidth >= wrapperScrollWidth) {
                return false;
            }
            doMove(setTransformLeft, offsetX);
        }
        else {
            if (wrapperHeight >= wrapperScrollHeight) {
                return false;
            }
            doMove(setTransformTop, offsetY);
        }
        clearTouchMoving();
        doLockAnimation();
        return true;
    });
    useEffect(() => {
        clearTouchMoving();
        if (lockAnimation) {
            touchMovingRef.current = window.setTimeout(() => {
                setLockAnimation(0);
            }, 100);
        }
        return clearTouchMoving;
    }, [lockAnimation]);
    // ========================= Scroll ========================
    function scrollToTab(key = activeKey) {
        const tabOffset = tabOffsets.get(key) || {
            width: 0,
            height: 0,
            left: 0,
            right: 0,
            top: 0,
        };
        if (tabPositionTopOrBottom) {
            // ============ Align with top & bottom ============
            let newTransform = transformLeft;
            // RTL
            if (rtl) {
                if (tabOffset.right < transformLeft) {
                    newTransform = tabOffset.right;
                }
                else if (tabOffset.right + tabOffset.width > transformLeft + wrapperWidth) {
                    newTransform = tabOffset.right + tabOffset.width - wrapperWidth;
                }
            }
            // LTR
            else if (tabOffset.left < -transformLeft) {
                newTransform = -tabOffset.left;
            }
            else if (tabOffset.left + tabOffset.width > -transformLeft + wrapperWidth) {
                newTransform = -(tabOffset.left + tabOffset.width - wrapperWidth);
            }
            setTransformTop(0);
            setTransformLeft(alignInRange(newTransform));
        }
        else {
            // ============ Align with left & right ============
            let newTransform = transformTop;
            if (tabOffset.top < -transformTop) {
                newTransform = -tabOffset.top;
            }
            else if (tabOffset.top + tabOffset.height > -transformTop + wrapperHeight) {
                newTransform = -(tabOffset.top + tabOffset.height - wrapperHeight);
            }
            setTransformLeft(0);
            setTransformTop(alignInRange(newTransform));
        }
    }
    // ========================== Tab ==========================
    // Render tab node & collect tab offset
    const [visibleStart, visibleEnd] = useVisibleRange(tabOffsets, {
        width: wrapperWidth,
        height: wrapperHeight,
        left: transformLeft,
        top: transformTop
    }, {
        width: wrapperContentWidth,
        height: wrapperContentHeight
    }, {
        width: addWidth,
        height: addHeight
    }, Object.assign(Object.assign({}, props), { tabs }));
    const tabNodes = tabs.map((tab, index) => {
        const { key } = tab;
        return (<TabNode id={id} prefixCls={prefixCls} key={key} rtl={rtl} tab={tab} closable={tab.closable} editable={editable} active={key === activeKey} tabPosition={tabPosition} tabBarGutter={tabBarGutter} renderWrapper={children} removeAriaLabel={locale === null || locale === void 0 ? void 0 : locale.removeAriaLabel} ref={getBtnRef(key)} onClick={e => {
                setScroll(true);
                onTabClick(key, e);
            }} onRemove={() => {
                removeBtnRef(key);
            }} onFocus={() => {
                scrollToTab(key);
                doLockAnimation();
                // Focus element will make scrollLeft change which we should reset back
                if (!rtl) {
                    tabsWrapperRef.current.scrollLeft = 0;
                }
                tabsWrapperRef.current.scrollTop = 0;
            }} onEditTabNav={tabContent => {
                const _tabs = cloneDeep(tabs);
                _tabs[index].tab = tabContent;
                onEditTabNav && onEditTabNav(_tabs);
            }}/>);
    });
    const onListHolderResize = useRaf(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        // Update wrapper records
        const offsetWidth = ((_a = tabsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
        const offsetHeight = ((_b = tabsWrapperRef.current) === null || _b === void 0 ? void 0 : _b.offsetHeight) || 0;
        const newAddWidth = ((_c = innerAddButtonRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth) || 0;
        const newAddHeight = ((_d = innerAddButtonRef.current) === null || _d === void 0 ? void 0 : _d.offsetHeight) || 0;
        const newOperationWidth = ((_e = operationsRef.current) === null || _e === void 0 ? void 0 : _e.offsetWidth) || 0;
        const newOperationHeight = ((_f = operationsRef.current) === null || _f === void 0 ? void 0 : _f.offsetHeight) || 0;
        setWrapperWidth(offsetWidth);
        setWrapperHeight(offsetHeight);
        setAddWidth(newAddWidth);
        setAddHeight(newAddHeight);
        const newWrapperScrollWidth = (((_g = tabListRef.current) === null || _g === void 0 ? void 0 : _g.offsetWidth) || 0) - newAddWidth;
        const newWrapperScrollHeight = (((_h = tabListRef.current) === null || _h === void 0 ? void 0 : _h.offsetHeight) || 0) - newAddHeight;
        setWrapperScrollWidth(newWrapperScrollWidth);
        setWrapperScrollHeight(newWrapperScrollHeight);
        const isOperationHidden = (_j = operationsRef.current) === null || _j === void 0 ? void 0 : _j.className.includes(operationsHiddenClassName);
        setWrapperContentWidth(newWrapperScrollWidth - (isOperationHidden ? 0 : newOperationWidth));
        setWrapperContentHeight(newWrapperScrollHeight - (isOperationHidden ? 0 : newOperationHeight));
        // Update buttons records
        setTabSizes(() => {
            const newSizes = new Map();
            tabs.forEach(({ key }) => {
                const btnNode = getBtnRef(key).current;
                if (btnNode) {
                    newSizes.set(key, {
                        width: btnNode.offsetWidth,
                        height: btnNode.offsetHeight,
                        left: btnNode.offsetLeft,
                        top: btnNode.offsetTop,
                    });
                }
            });
            return newSizes;
        });
    });
    // ======================== Dropdown =======================
    const startHiddenTabs = tabs.slice(0, visibleStart);
    const endHiddenTabs = tabs.slice(visibleEnd + 1);
    const hiddenTabs = [...startHiddenTabs, ...endHiddenTabs];
    // =================== Link & Operations ===================
    const [inkStyle, setInkStyle] = useState();
    const activeTabOffset = tabOffsets.get(activeKey);
    // Delay set ink style to avoid remove tab blink
    const inkBarRafRef = useRef();
    function cleanInkBarRaf() {
        raf.cancel(inkBarRafRef.current);
    }
    useEffect(() => {
        const newInkStyle = {};
        if (activeTabOffset) {
            if (tabPositionTopOrBottom) {
                if (rtl) {
                    newInkStyle.right = activeTabOffset.right;
                }
                else {
                    newInkStyle.left = activeTabOffset.left;
                }
                newInkStyle.width = activeTabOffset.width;
            }
            else {
                newInkStyle.top = activeTabOffset.top;
                newInkStyle.height = activeTabOffset.height;
            }
        }
        cleanInkBarRaf();
        inkBarRafRef.current = raf(() => {
            setInkStyle(newInkStyle);
        });
        return cleanInkBarRaf;
    }, [activeTabOffset, tabPositionTopOrBottom, rtl]);
    // ========================= Effect ========================
    useEffect(() => {
        if (isScroll) {
            scrollToTab();
        }
    }, [activeKey, activeTabOffset, tabOffsets, tabPositionTopOrBottom, isScroll]);
    // Should recalculate when rtl changed
    useEffect(() => {
        onListHolderResize();
    }, [rtl, tabBarGutter, activeKey, tabs.map(tab => tab.key).join('_')]);
    // ========================= Render ========================
    const hasDropdown = !!hiddenTabs.length;
    const wrapPrefix = `${prefixCls}-nav-wrap`;
    let pingLeft;
    let pingRight;
    let pingTop;
    let pingBottom;
    if (tabPositionTopOrBottom) {
        if (rtl) {
            pingRight = transformLeft > 0;
            pingLeft = transformLeft + wrapperWidth < wrapperScrollWidth;
        }
        else {
            pingLeft = transformLeft < 0;
            pingRight = -transformLeft + wrapperWidth < wrapperScrollWidth;
        }
    }
    else {
        pingTop = transformTop < 0;
        pingBottom = -transformTop + wrapperHeight < wrapperScrollHeight;
    }
    // operations
    const [scrolledKey, setScrolledKey] = useState(null);
    const popupId = `${id}-more-popup`;
    const moreStyle = {
        [rtl ? 'marginRight' : 'marginLeft']: tabBarGutter
    };
    moreStyle.cursor = 'pointer';
    if (!hiddenTabs.length) {
        moreStyle.visibility = 'hidden';
        moreStyle.order = 1;
    }
    const onMoreClick = type => {
        const index = findIndex(tabs, tab => tab.key === scrolledKey);
        let key;
        if (type === 'left') {
            key = tabs[index - 1 <= 0 ? 0 : index - 1].key;
        }
        else {
            key = tabs[index + 1 > tabs.length - 1 ? tabs.length - 1 : index + 1].key;
        }
        setScrolledKey(key);
        const tabOffset = tabOffsets.get(key) || {
            width: 0,
            height: 0,
            left: 0,
            right: 0,
            top: 0,
        };
        let newTransform = transformLeft;
        if (type === 'left') {
            newTransform = transformLeft + tabOffset.width + 16;
        }
        else {
            newTransform = transformLeft - tabOffset.width - 16;
        }
        setTransformLeft(alignInRange(newTransform));
        setScroll(false);
    };
    const moreLeftNode = mobile ? null : (<button type="button" className={`${prefixCls}-nav-more left`} style={moreStyle} tabIndex={-1} aria-hidden="true" aria-haspopup="listbox" aria-controls={`${popupId}-left`} id={`${id}-more`} onClick={() => onMoreClick('left')}>
            <OutlinedLeft />
        </button>);
    const moreRightNode = mobile ? null : (<button type="button" className={`${prefixCls}-nav-more right`} style={moreStyle} tabIndex={-1} aria-hidden="true" aria-haspopup="listbox" aria-controls={`${popupId}-right`} id={`${id}-more`} 
    // aria-expanded={open}
    onClick={() => onMoreClick('right')}>
            <OutlinedRight />
        </button>);
    useEffect(() => {
        setScrolledKey(activeKey);
    }, [activeKey]);
    return (<div ref={ref} role="tablist" className={classNames(`${prefixCls}-nav`, className)} style={style} onKeyDown={() => {
            // No need animation when use keyboard
            doLockAnimation();
        }}>
            
            <ExtraContent position="left" extra={extra} prefixCls={prefixCls}/>

            <div className={classNames(`${prefixCls}-nav-operations`, !hasDropdown && operationsHiddenClassName)}>
                {moreLeftNode}
            </div>

            <ResizeObserver onResize={onListHolderResize}>
                <div className={classNames(wrapPrefix, {
            [`${wrapPrefix}-ping-left`]: pingLeft,
            [`${wrapPrefix}-ping-right`]: pingRight,
            [`${wrapPrefix}-ping-top`]: pingTop,
            [`${wrapPrefix}-ping-bottom`]: pingBottom,
        })} ref={tabsWrapperRef}>
                    <ResizeObserver onResize={onListHolderResize}>
                        <div ref={tabListRef} className={`${prefixCls}-nav-list`} style={{
            transform: `translate(${transformLeft}px, ${transformTop}px)`,
            transition: lockAnimation ? 'none' : undefined
        }}>
                            {tabNodes}
                            <AddButton ref={innerAddButtonRef} prefixCls={prefixCls} locale={locale} editable={editable} style={{ visibility: hasDropdown ? 'hidden' : null }} onClick={() => setScroll(true)}/>

                            <div className={classNames(`${prefixCls}-ink-bar`, {
            [`${prefixCls}-ink-bar-animated`]: animated.inkBar,
        })} style={inkStyle}/>
                        </div>
                    </ResizeObserver>
                </div>
            </ResizeObserver>

            <div className={classNames(`${prefixCls}-nav-operations`, !hasDropdown && operationsHiddenClassName)}>
                {moreRightNode}
                <AddButton prefixCls={prefixCls} locale={locale} editable={editable} onClick={() => setScroll(true)}/>
            </div>

            <ExtraContent position="right" extra={extra} prefixCls={prefixCls}/>
        </div>);
    /* eslint-enable */
}
export default React.forwardRef(TabNavList);
